import {Component, OnDestroy, OnInit} from '@angular/core';
import {MatDialog}                    from '@angular/material/dialog';
import {MatSnackBar}                  from '@angular/material/snack-bar';
import {Subject}                      from 'rxjs';
import {finalize, takeUntil}          from 'rxjs/operators';
import {FormOptionGroupService}       from '@src/app/shared/services/form-option-group.service';
import {FormOptionGroup}              from '@src/app/shared/models/option-group.model';
import {DialogComponent}              from '@src/app/shared/components/dialog/dialog.component';
import {OptionGroupFormComponent}     from './option-group-form.component';

@Component({
    standalone: false,
    selector: 'app-option-groups',
    templateUrl: './option-groups.component.html',
    styleUrls: ['./option-groups.component.scss']
})
export class OptionGroupsComponent implements OnInit, OnDestroy
{
    public displayedColumns: string[] = ['name', 'options', 'type', 'actions'];
    public optionGroups: FormOptionGroup[] = [];
    public isLoading = false;

    private readonly destroy$ = new Subject<void>();

    public constructor(
        private optionGroupService: FormOptionGroupService,
        private dialog: MatDialog,
        private snackBar: MatSnackBar,
    )
    {
    }

    public ngOnInit(): void
    {
        this.optionGroupService.optionGroups$
            .pipe(takeUntil(this.destroy$))
            .subscribe(groups => this.optionGroups = groups);

        this.loadOptionGroups();
    }

    public ngOnDestroy(): void
    {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public loadOptionGroups(): void
    {
        this.isLoading = true;
        this.optionGroupService
            .refreshOptionGroups({scope: 'all', reusable: true, with: 'options'})
            .pipe(finalize(() => this.isLoading = false))
            .subscribe({
                error: () => this.snackBar.open('Failed to load option groups.', 'Ok', {duration: 3500})
            });
    }

    public optionsPreview(group: FormOptionGroup): string
    {
        const labels = (group.options ?? []).map(option => option.label);
        if (!labels.length) {
            return 'No options';
        }

        const preview = labels.slice(0, 3).join(', ');
        return labels.length > 3 ? `${preview} +${labels.length - 3} more` : preview;
    }

    public isSystem(group: FormOptionGroup): boolean
    {
        return !group.tenant_id;
    }

    public openDialog(group: FormOptionGroup | null = null): void
    {
        const dialogRef = this.dialog.open(OptionGroupFormComponent, {
            width: '50%',
            panelClass: 'mobile-width',
            disableClose: true,
            data: {group},
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.snackBar.open(group ? 'Option group updated.' : 'Option group created.', 'Ok', {duration: 2500});
            }
        });
    }

    public confirmDelete(group: FormOptionGroup): void
    {
        const confirmDialogRef = this.dialog.open(DialogComponent, {
            data: {
                title: 'Delete Option Group',
                message: `Are you sure you want to delete "${group.name}"? Fields already using it keep their existing options, but it will stop being offered as a reusable option group.`,
                type: 'danger-outline',
                confirmButtonLabel: 'Delete'
            }
        });

        confirmDialogRef.afterClosed().subscribe(confirm => {
            if (confirm) {
                this.optionGroupService.deleteOptionGroup(group.id).subscribe({
                    next: () => this.snackBar.open('Option group deleted.', 'Ok', {duration: 2500}),
                    error: response => {
                        const message = response?.error?.message ?? 'Failed to delete option group.';
                        this.snackBar.open(message, 'Ok', {duration: 4000});
                    }
                });
            }
        });
    }
}
